/**
 * useKnowledgeNode - Hook for managing KnowledgeNode state and API interactions
 *
 * Handles:
 * - Fetching the board's knowledge space
 * - Fetching and displaying documents
 * - Uploading new documents
 * - Asking questions and streaming answers to BlockNode
 */

import { useState, useCallback, useEffect } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type * as Y from 'yjs';
import type {
  KnowledgeSpace,
  KnowledgeDocument,
  KnowledgeCitation,
  KnowledgeCitationSupport,
  KnowledgeNodeStatus,
} from '@deep-agents/shared-types';
import { useOutputNode } from './useOutputNode';

// ============================================================================
// TYPES
// ============================================================================

export interface UseKnowledgeNodeOptions {
  /** ID of this KnowledgeNode */
  nodeId: string;

  /** Board ID for fetching the knowledge space */
  boardId: string;

  /** Yjs map of nodes */
  nodesMap: Y.Map<Node>;

  /** Yjs map of edges */
  edgesMap: Y.Map<Edge>;

  /** Yjs document for collaboration */
  ydoc: Y.Doc;

  /** Wrapper for undo/redo support */
  makeChange: (fn: () => void) => void;
}

export interface UseKnowledgeNodeResult {
  /** Current knowledge space (null if not loaded) */
  space: KnowledgeSpace | null;

  /** List of documents in the space */
  documents: KnowledgeDocument[];

  /** Current node status */
  status: KnowledgeNodeStatus;

  /** Error message if status is 'error' */
  error: string | null;

  /** Last question asked */
  lastQuestion: string;

  /** Last answer received (for inline display) */
  lastAnswer: string;

  /** Citations from last answer */
  lastCitations: KnowledgeCitation[];

  /** Citation supports for inline citation markers */
  lastCitationSupports: KnowledgeCitationSupport[];

  /** Whether output port is connected to a BlockNode */
  isOutputConnected: boolean;

  /** Fetch the board's knowledge space */
  fetchSpace: () => Promise<void>;

  /** Refresh documents list */
  refreshDocuments: () => Promise<void>;

  /** Upload a document to the space */
  uploadDocument: (file: File) => Promise<void>;

  /** Ask a question */
  ask: (question: string) => Promise<void>;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useKnowledgeNode(options: UseKnowledgeNodeOptions): UseKnowledgeNodeResult {
  const { nodeId, boardId, nodesMap, edgesMap, ydoc, makeChange } = options;

  // State
  const [space, setSpace] = useState<KnowledgeSpace | null>(null);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [status, setStatus] = useState<KnowledgeNodeStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastQuestion, setLastQuestion] = useState('');
  const [lastAnswer, setLastAnswer] = useState('');
  const [lastCitations, setLastCitations] = useState<KnowledgeCitation[]>([]);
  const [lastCitationSupports, setLastCitationSupports] = useState<KnowledgeCitationSupport[]>([]);

  // Use output node utilities
  const {
    isOutputConnected,
    getConnectedBlockId,
    findOrCreateTargetBlock,
    setBlockContent,
    clearBlock,
  } = useOutputNode({
    sourceNodeId: nodeId,
    nodesMap,
    edgesMap,
    ydoc,
    makeChange,
  });

  // Check if connected
  const outputConnected = isOutputConnected();

  /**
   * Fetch the board's knowledge space
   */
  const fetchSpace = useCallback(async () => {
    if (!boardId) return;

    setStatus('loading');
    setError(null);

    try {
      const response = await fetch(`/api/kb/boards/${boardId}/space`, {
        credentials: 'include',
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to fetch knowledge space');
      }

      setSpace(data?.data?.space || null);

      // Also fetch documents if space exists
      if (data?.data?.space?.id) {
        await fetchDocuments(data.data.space.id);
      }

      setStatus('idle');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch space';
      setError(errorMessage);
      setStatus('error');
    }
  }, [boardId]);

  /**
   * Fetch documents for a space
   */
  const fetchDocuments = async (spaceId: string) => {
    try {
      const response = await fetch(`/api/kb/spaces/${spaceId}/documents`, {
        credentials: 'include',
      });
      const data = await response.json();

      if (response.ok) {
        setDocuments(data?.data?.documents || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  /**
   * Refresh documents list
   */
  const refreshDocuments = useCallback(async () => {
    if (!space?.id) return;
    await fetchDocuments(space.id);
  }, [space?.id]);

  /**
   * Upload a document to the space
   */
  const uploadDocument = useCallback(async (file: File) => {
    if (!space?.id) {
      setError('No knowledge space available');
      return;
    }

    setStatus('loading');
    setError(null);

    try {
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);

      const response = await fetch(`/api/kb/spaces/${space.id}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to upload document');
      }

      // Refresh documents list
      await refreshDocuments();
      setStatus('idle');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      setStatus('error');
    }
  }, [space?.id, refreshDocuments]);

  /**
   * Ask a question
   */
  const ask = useCallback(async (question: string) => {
    if (!space?.id || !question.trim()) return;

    setStatus('asking');
    setError(null);
    setLastQuestion(question);
    setLastAnswer('');
    setLastCitations([]);
    setLastCitationSupports([]);

    try {
      const response = await fetch(`/api/kb/spaces/${space.id}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to get answer');
      }

      const answer = data?.data?.answer || 'No answer found.';
      const citations = data?.data?.citations || [];
      const citationSupports = data?.data?.citationSupports || [];

      // Store for inline display
      setLastAnswer(answer);
      setLastCitations(citations);
      setLastCitationSupports(citationSupports);

      // If connected to a BlockNode, write there
      const connectedBlockId = getConnectedBlockId();
      if (connectedBlockId) {
        // Convert answer to HTML paragraphs
        const htmlContent = answer
          .split('\n\n')
          .map((para: string) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
          .join('');

        setBlockContent(connectedBlockId, htmlContent, citations);
      }

      setStatus('idle');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get answer';
      setError(errorMessage);
      setStatus('error');
    }
  }, [space?.id, getConnectedBlockId, setBlockContent]);

  // Fetch space on mount
  useEffect(() => {
    if (boardId) {
      fetchSpace();
    }
  }, [boardId, fetchSpace]);

  return {
    space,
    documents,
    status,
    error,
    lastQuestion,
    lastAnswer,
    lastCitations,
    lastCitationSupports,
    isOutputConnected: outputConnected,
    fetchSpace,
    refreshDocuments,
    uploadDocument,
    ask,
  };
}
